import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBriefcase, FaSearch, FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './SearchContainer.css';

function SearchContainer({ className = '', variant = 'default' }) {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    category: '',
    jobType: ''
  });

  const jobTypes = [
    { value: 'full-time', label: 'Full Time', icon: '💼' },
    { value: 'part-time', label: 'Part Time', icon: '⌚' },
    { value: 'internship', label: 'Internship', icon: '🎓' },
    { value: 'freelance', label: 'Freelance', icon: '🌐' }
  ];

  const categories = [
    { value: 'accounting_finance', label: 'Accounting & Finance' },
    { value: 'data_analyst', label: 'Data Analyst' },
    { value: 'banking', label: 'Banking' },
    { value: 'bpo', label: 'BPO' },
    { value: 'business_development', label: 'Business Development' },
    { value: 'customer_support', label: 'Customer Support' },
    { value: 'health_care', label: 'Health Care' },
    { value: 'management', label: 'Management' },
    { value: 'digital_marketing', label: 'Digital Marketing' },
    { value: 'electrical_electronics', label: 'Electrical & Electronics' },
    { value: 'engineering_technology', label: 'Engineering & Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'human_resource', label: 'Human Resource' },
    { value: 'it_software', label: 'IT & Software' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'research_development', label: 'Research & Development' },
    { value: 'retail_ecommerce', label: 'Retail & Ecommerce' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleJobTypeSelect = (value) => {
    setSearchParams(prev => ({
      ...prev,
      jobType: value
    }));
    setIsDropdownOpen(false);
  };

  const handleCategorySelect = (value) => {
    setSearchParams(prev => ({
      ...prev,
      category: value
    }));
    setIsCategoryDropdownOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchParams.keyword) params.append('keyword', searchParams.keyword);
    if (searchParams.category) params.append('category', searchParams.category);
    if (searchParams.jobType) params.append('type', searchParams.jobType);
    
    navigate({
      pathname: '/search',
      search: params.toString()
    });
  };

  return (
    <motion.div
      className="search-container-main"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-fields-wrapper">
          {/* Keyword Input */}
          <div className={`search-field ${focusedInput === 'keyword' ? 'focused' : ''}`}>
            <div className="field-icon">
              <FaBriefcase className="icon" />
            </div>
            <div className="field-content">
              <label className="field-label">Job Position</label>
              <input
                type="text"
                name="keyword"
                value={searchParams.keyword}
                onChange={handleInputChange}
                onFocus={() => setFocusedInput('keyword')}
                onBlur={() => setFocusedInput(null)}
                placeholder="e.g. UI/UX Designer, Developer"
                className="field-input"
              />
            </div>
          </div>

          {/* Category Dropdown */}
          <div className="search-field">
            <div 
              className="dropdown-trigger"
              onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
            >
              <div className="field-content">
                <label className="field-label">Category</label>
                <div className="selected-type">
                  {searchParams.category ? 
                    categories.find(c => c.value === searchParams.category)?.label :
                    "Select Category"
                  }
                </div>
              </div>
              <FaChevronDown className={`dropdown-arrow ${isCategoryDropdownOpen ? 'rotated' : ''}`} />
            </div>

            <AnimatePresence>
              {isCategoryDropdownOpen && (
                <motion.div 
                  className="dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {categories.map(category => (
                    <button
                      key={category.value}
                      type="button"
                      onClick={() => handleCategorySelect(category.value)}
                      className="dropdown-item"
                    >
                      {category.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Job Type Dropdown */}
          <div className="search-field">
            <div 
              className="dropdown-trigger"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="field-content">
                <label className="field-label">Job Type</label>
                <div className="selected-type">
                  {searchParams.jobType ? 
                    <>
                      <span className="type-icon">
                        {jobTypes.find(t => t.value === searchParams.jobType)?.icon}
                      </span>
                      <span>{jobTypes.find(t => t.value === searchParams.jobType)?.label}</span>
                    </> :
                    "Select Job Type"
                  }
                </div>
              </div>
              <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`} />
            </div>

            <AnimatePresence>
              {isDropdownOpen && (
                <motion.div 
                  className="dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {jobTypes.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleJobTypeSelect(type.value)}
                      className="dropdown-item"
                    >
                      <span className="type-icon">{type.icon}</span>
                      {type.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Search Button */}
          <button type="submit" className="search-button">
            <FaSearch className="search-icon" />
            <span>Search Jobs</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
}

export default SearchContainer; 